/**
 * Top 20 Questions Suggérées pour l'AI Assistant PRIsME
 * 
 * Ces questions guident les Primes/Utilisateurs vers les bonnes réponses
 * et démontrent les capacités de l'assistant.
 */

export interface SuggestedQuestion {
  id: string;
  category: string;
  icon: string;
  question: string;
  relatedKnowledge: string[]; // IDs des sections de knowledge liées
}

export const suggestedQuestions: SuggestedQuestion[] = [
  // ========================================
  // CATÉGORIE: Démarrage / Premiers Pas
  // ========================================
  {
    id: 'q1',
    category: 'Démarrage',
    icon: '🚀',
    question: "Mon client demande un plan TI, par où commencer?",
    relatedKnowledge: ['methodologie-prisme', 'diagnostic-ti', 'plan-directeur']
  },
  {
    id: 'q2',
    category: 'Démarrage',
    icon: '🎯',
    question: "Comment structurer une première rencontre avec un nouveau client TI?",
    relatedKnowledge: ['methodologie-prisme', 'livrables-tangibles']
  },
  {
    id: 'q3',
    category: 'Démarrage',
    icon: '💡',
    question: "Quelle est la différence entre Plan Directeur, ODS et Roadmap?",
    relatedKnowledge: ['livrables-tangibles', 'plan-directeur']
  },

  // ========================================
  // CATÉGORIE: IA & Innovation
  // ========================================
  {
    id: 'q4',
    category: 'IA & Innovation',
    icon: '🤖',
    question: "Comment intégrer l'IA dans une PME qui n'a aucune maturité technologique?",
    relatedKnowledge: ['diagnostic-ti', 'methodologie-prisme']
  },
  {
    id: 'q5',
    category: 'IA & Innovation',
    icon: '🔮',
    question: "Mon client veut de l'IA mais ne sait pas pour quoi faire. Comment le guider?",
    relatedKnowledge: ['methodologie-prisme', 'diagnostic-ti']
  },
  {
    id: 'q6',
    category: 'IA & Innovation',
    icon: '⚡',
    question: "Quels sont les quick wins en IA pour impressionner un client rapidement?",
    relatedKnowledge: ['diagnostic-ti', 'amelioration-continue']
  },

  // ========================================
  // CATÉGORIE: Diagnostic & Évaluation
  // ========================================
  {
    id: 'q7',
    category: 'Diagnostic',
    icon: '📊',
    question: "Quand utiliser le Diagnostic TI vs un Plan Directeur complet?",
    relatedKnowledge: ['diagnostic-ti', 'plan-directeur', 'methodologie-prisme']
  },
  {
    id: 'q8',
    category: 'Diagnostic',
    icon: '🔒',
    question: "Mon client est inquiet de sa cybersécurité. Que lui proposer?",
    relatedKnowledge: ['diagnostic-ti', 'methodologie-prisme']
  },
  {
    id: 'q9',
    category: 'Diagnostic',
    icon: '📈',
    question: "Comment présenter un score de maturité TI sans décourager le client?",
    relatedKnowledge: ['diagnostic-ti', 'livrables-tangibles']
  },

  // ========================================
  // CATÉGORIE: Ventes & Propositions
  // ========================================
  {
    id: 'q10',
    category: 'Ventes',
    icon: '💰',
    question: "Comment pricer un Diagnostic TI? Quelle est la fourchette acceptable?",
    relatedKnowledge: ['diagnostic-ti', 'generateur-propositions']
  },
  {
    id: 'q11',
    category: 'Ventes',
    icon: '📝',
    question: "Comment structurer une proposition commerciale qui convertit?",
    relatedKnowledge: ['generateur-propositions', 'methodologie-prisme']
  },
  {
    id: 'q12',
    category: 'Ventes',
    icon: '🎁',
    question: "Quels livrables tangibles offrir lors d'une première rencontre gratuite?",
    relatedKnowledge: ['livrables-tangibles', 'diagnostic-ti']
  },

  // ========================================
  // CATÉGORIE: Gouvernance & Processus
  // ========================================
  {
    id: 'q13',
    category: 'Gouvernance',
    icon: '🏛️',
    question: "Comment convaincre un client de mettre en place un Comité TI?",
    relatedKnowledge: ['gouvernance-comite-ti', 'plan-directeur']
  },
  {
    id: 'q14',
    category: 'Gouvernance',
    icon: '📅',
    question: "Que doit contenir un PV de Comité TI efficace?",
    relatedKnowledge: ['gouvernance-comite-ti', 'livrables-tangibles']
  },
  {
    id: 'q15',
    category: 'Gouvernance',
    icon: '🎯',
    question: "Comment prioriser les projets TI quand tout semble urgent?",
    relatedKnowledge: ['gouvernance-comite-ti', 'plan-directeur']
  },

  // ========================================
  // CATÉGORIE: Excellence & Standards
  // ========================================
  {
    id: 'q16',
    category: 'Excellence',
    icon: '💎',
    question: "Quels sont les standards d'excellence attendus d'un Prime?",
    relatedKnowledge: ['referentiel-prime', 'amelioration-continue']
  },
  {
    id: 'q17',
    category: 'Excellence',
    icon: '📚',
    question: "Comment structurer ma prise de besoin pour ne rien oublier?",
    relatedKnowledge: ['methodologie-prisme', 'diagnostic-ti']
  },
  {
    id: 'q18',
    category: 'Excellence',
    icon: '🌱',
    question: "Comment rester à jour avec les nouvelles technologies et frameworks?",
    relatedKnowledge: ['amelioration-continue', 'referentiel-prime']
  },

  // ========================================
  // CATÉGORIE: Challenges Spécifiques
  // ========================================
  {
    id: 'q19',
    category: 'Challenges',
    icon: '⚠️',
    question: "Mon client n'a aucun budget TI. Comment l'aider quand même?",
    relatedKnowledge: ['diagnostic-ti', 'methodologie-prisme']
  },
  {
    id: 'q20',
    category: 'Challenges',
    icon: '🔧',
    question: "Le client veut tout faire lui-même mais manque de compétences. Que faire?",
    relatedKnowledge: ['referentiel-prime', 'gouvernance-comite-ti']
  }
];

/**
 * Obtient les questions par catégorie
 */
export function getQuestionsByCategory(category: string): SuggestedQuestion[] {
  return suggestedQuestions.filter(q => q.category === category);
}

/**
 * Obtient toutes les catégories uniques
 */
export function getQuestionCategories(): string[] {
  return [...new Set(suggestedQuestions.map(q => q.category))];
}

/**
 * Obtient une question par ID
 */
export function getQuestionById(id: string): SuggestedQuestion | undefined {
  return suggestedQuestions.find(q => q.id === id);
}

